import { normalizePhaseApplicability, phaseApplicabilityAllows, recordedPhaseContext } from './phaseApplicability.js';

export const PHASE_EXECUTION_VERSION = 1;
export const PHASE_EXECUTION_ERROR = 'PHASE_EXECUTION_REVIEW_REQUIRED';

export function phaseExecutionError(message) {
  const error = new Error(message);
  error.code = PHASE_EXECUTION_ERROR;
  return error;
}

export function isPhaseExecutionError(error) {
  return error?.code === PHASE_EXECUTION_ERROR;
}

/** Activation is server-owned. Phase names or draft applicability alone are inert. */
export function resolveDomainPhasePolicy({ schoolId, domainId, settings, domain }) {
  const execution = settings?.domainExecutions?.[domainId];
  if (execution == null) return { enabled: false, schoolId, domainId };
  if (execution.version !== PHASE_EXECUTION_VERSION || execution.state !== 'active'
    || !Number.isInteger(execution.revision) || execution.revision < 1) {
    throw phaseExecutionError('This Domain’s phase process needs review before processing can continue.');
  }
  if (!domain || (domain.schoolId && domain.schoolId !== schoolId)) {
    throw phaseExecutionError('The school’s phase process configuration is unavailable.');
  }
  try {
    return { enabled: true, schoolId, domainId, revision: execution.revision,
      applicability: normalizePhaseApplicability(domain.phaseApplicability) };
  } catch (error) {
    throw phaseExecutionError(error.message);
  }
}

/** `read` is a school-scoped server read, or the creating transaction's read. */
export async function loadDomainPhasePolicy({ schoolId, domainId, read }) {
  const settings = await read('schoolPhaseSettings', 'current');
  const domain = settings?.domainExecutions?.[domainId] == null ? null : await read('domains', domainId);
  return resolveDomainPhasePolicy({ schoolId, domainId, settings, domain });
}

/** Use recorded context for both the trigger and every counted historical record. */
export function phaseItemAllowsRecord(policy, item, record, { historical = false } = {}) {
  if (!policy?.enabled) return true;
  if ((record?.schoolId && record.schoolId !== policy.schoolId) ||
    (item?.schoolId && item.schoolId !== policy.schoolId)) return false;
  let applicability;
  try { applicability = normalizePhaseApplicability(item?.phaseApplicability); }
  catch (error) { throw phaseExecutionError(error.message); }
  const context = recordedPhaseContext(record, policy.schoolId);
  const known = context.status === 'known' && context.studentId === record?.studentId;
  const restricted = applicability.mode === 'selected-phases' || policy.applicability.mode === 'selected-phases';
  if (restricted && !known) {
    // Explicitly unassigned learners simply do not belong to a selected phase.
    if (historical || context.status === 'unassigned') return false;
    throw phaseExecutionError('Resolve this learner’s phase at the time of the event before applying phase-specific rules.');
  }
  return phaseApplicabilityAllows(applicability, known ? context : null, policy.applicability);
}

/** Existing group targeting remains an additional condition, never a phase override. */
export function phaseRuleAllowsRecord(policy, rule, record, options) {
  if (!policy?.enabled) return true;
  if (!phaseItemAllowsRecord(policy, rule, record, options)) return false;
  if (rule.groups == null) return true;
  if (!Array.isArray(rule.groups)) throw phaseExecutionError('This rule’s learner groups need review.');
  if (!rule.groups.length) return true;
  const context = recordedPhaseContext(record, policy.schoolId);
  const identities = [context.gradeId ?? record.gradeId ?? record.grade, record.groupId, context.classId ?? record.classId]
    .filter(value => value != null).map(String);
  return rule.groups.some(group => identities.includes(String(group)));
}

export function assertPhaseItemAvailable(policy, item, record, label = 'This item') {
  if (!phaseItemAllowsRecord(policy, item, record)) {
    throw phaseExecutionError(`${label} is not available for this learner’s phase.`);
  }
}
